import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNetworkContext } from '../../network';
import { useNotificationToast } from '../../notification';
import {
  ConversationData,
  CustomersReportResponse,
  dispatch,
  getCatalogDownloadStatusForCustomer,
  getCheckInInfo,
  getClockInInfo,
  getExtraDetails,
  getWorkspaceId,
  updateChannel,
  updateExtraDetails,
  useAppSelector,
  useLazyGetChannelDetailsQuery,
  useLazyGetConversationsQuery,
} from '@zonofi/zono-money-store';
import { ReactComponent as DownloadIcon } from '@zonofi/svg-icons/download.svg';
import { useCustomerProfileFetcher } from './use-customer-profile-fetcher';
import { isEmpty } from 'lodash';
import { useCustomNavigate } from '../../helper';
import { DisplayNotificationObject } from '../../types';
import { FIELD_OPS_ROUTES } from '../../constants';
import { SVGS } from '../../assets';

export const useCustomerProfileScreen = () => {
  const { onBack, navigate } = useCustomNavigate();
  const { customerId } = useParams();

  const catalogDownloadData = useAppSelector(
    getCatalogDownloadStatusForCustomer
  );

  const clockInInfo = useAppSelector(getClockInInfo);
  const checkInInfo = useAppSelector(getCheckInInfo);
  const workspaceId = useAppSelector(getWorkspaceId);
  const { networkStatus: isOnline } = useNetworkContext();
  const customerDetails = useAppSelector(getExtraDetails);

  const catalogDownloaded = catalogDownloadData(customerId ?? '');
  // NEW: priority-based API fetcher
  const { fetchProfileHighPriority } = useCustomerProfileFetcher();
  const [getConversationsApi, conversationsApi] =
    useLazyGetConversationsQuery();
  const [channelDetailsApi] = useLazyGetChannelDetailsQuery();

  const [customerProfileData, setCustomerProfileData] =
    useState<CustomersReportResponse>();
  const [loading, setLoading] = useState(true);
  const { onShowToast, onCloseToast } = useNotificationToast();

  const displayNotification = useCallback(
    (payload: { title: string; body: string }) => {
      const data = payload as unknown as DisplayNotificationObject;

      onShowToast({
        body: data.body,
        title: data.title,
      });

      setTimeout(() => {
        onCloseToast();
      }, 8000);
    },
    []
  );

  /**
   * Fetch customer profile (HIGH priority) when user opens a profile screen.
   * This ensures:
   * ✔ Cached result -> instant
   * ✔ If queue is busy -> user jumps ahead to front of queue
   * ✔ No blocking on background sync tasks
   */
  const fetchCustomerProfile = useCallback(() => {
    if (!customerId) return;
    fetchProfileHighPriority(customerId)
      .then((data) => {
        if (data.length > 0) setCustomerProfileData(data[0]);
      })
      .catch((err) => {
        console.error('Error fetching customer profile (screen open): ', err);
      })
      .finally(() => setLoading(false));
    getConversationsApi({
      sellerWorkspaceId: workspaceId,
      customerId,
      limit: 10,
    });
    channelDetailsApi({
      workspaceId,
      sellerWorkspaceId: workspaceId,
      customerId,
      channelType: 'C',
    })
      .unwrap()
      ?.then((res) => {
        dispatch(updateChannel(res));
      });
  }, [customerId]);

  useEffect(() => {
    fetchCustomerProfile();
  }, [fetchCustomerProfile]);

  const customerProfile = useMemo(
    () => customerProfileData,
    [customerProfileData]
  );

  useEffect(() => {
    if (!customerProfile) return;
    dispatch(
      updateExtraDetails({
        customerName: customerProfile?.customerName,
      })
    );
  }, [customerProfile]);

  const activeConversations = useMemo(() => {
    const conversations = conversationsApi?.data?.data ?? [];

    return conversations
      .filter((conversation: ConversationData) => {
        const status = String(conversation?.status ?? '').toLowerCase();
        return !status.includes('close');
      })
      .slice(0, 1)
      .map((conversation: ConversationData) => ({
        ...conversation,
        status: conversation.status ?? 'Open',
        createdAt: conversation.createdAt || conversation.lastMessageAt || '',
        tags: Array.isArray(conversation.metadata?.tags)
          ? conversation.metadata.tags
          : Array.isArray(conversation.metadata?.tag)
            ? conversation.metadata.tag
            : [],
      }));
  }, [conversationsApi?.data?.data]);

  const modules: {
    moduleName: string;
    icon: string;
    extraIcon?: JSX.Element;
    onClick: () => void;
    disabled?: boolean;
  }[] = useMemo(
    () => [
      {
        moduleName: 'Conversations',
        icon: SVGS.conversationModuleIcon,
        onClick: () =>
          navigate(
            FIELD_OPS_ROUTES.ISSUES?.replace(':customerId', customerId ?? '')
          ),
      },
      {
        moduleName: 'Products',
        icon: SVGS.ProductsIcon,
        ...(catalogDownloaded && {
          extraIcon: <DownloadIcon color="#D9D9D9" width={16} />,
        }),
        onClick: () => {
          if (catalogDownloaded || isOnline) {
            navigate(
              FIELD_OPS_ROUTES.PRODUCTS_LIST?.replace(
                ':customerId',
                customerId ?? ''
              ),
              {
                state: {
                  title: 'Transactions',
                  subTitle: customerDetails?.customerName ?? '',
                },
              }
            );
          } else {
            displayNotification({
              title: 'Missing Catalog Data',
              body: 'Please download product catalog for this customer first.',
            });
          }
        },
        disabled: !catalogDownloaded && !isOnline,
      },
      {
        moduleName: 'Orders',
        icon: SVGS.OrdersIcon_,
        onClick: () => {
          navigate(
            FIELD_OPS_ROUTES.CUSTOMER_PROFILE_ORDER.replace(
              ':customerId',
              customerId ?? ''
            )
          );
        },
      },
      {
        moduleName: 'Payments',
        icon: SVGS.PaymentsIcon,
        onClick: () => {
          navigate(
            FIELD_OPS_ROUTES.CUSTOMER_PROFILE_PAYMENT.replace(
              ':customerId',
              customerId ?? ''
            )
          );
        },
      },
      {
        moduleName: 'Invoices',
        icon: SVGS.InvoicesIcon,
        onClick: () => {
          navigate(
            FIELD_OPS_ROUTES.CUSTOMER_PROFILE_INVOICE.replace(
              ':customerId',
              customerId ?? ''
            )
          );
        },
      },
      {
        moduleName: 'Ledger',
        icon: SVGS.LedgerIcon,
        onClick: () => {
          const routeToNavigate = FIELD_OPS_ROUTES.LEDGER_LIST.replace(
            ':customerId',
            customerId!
          );
          navigate(routeToNavigate, {
            state: {
              title: 'Transactions',
              subTitle: customerDetails?.customerName ?? '',
            },
          });
        },
      },
    ],
    [catalogDownloaded, customerId, customerDetails, isOnline, customerProfile]
  );

  const resolvedCustomerName = useMemo(() => {
    const profileName = String(customerProfile?.customerName ?? '').trim();
    if (profileName) return profileName;

    const fallbackName = String(
      customerDetails?.customerName ?? customerDetails?.companyName ?? ''
    ).trim();
    return fallbackName;
  }, [
    customerProfile?.customerName,
    customerDetails?.customerName,
    customerDetails?.companyName,
  ]);

  const resolvedMobileNumber = useMemo(() => {
    if (!customerDetails?.mobile) return '';
    return customerDetails?.mobile.startsWith('+')
      ? customerDetails?.mobile
      : `+${customerDetails?.mobile}`;
  }, [customerDetails?.mobile]);

  return {
    address: customerDetails?.address || {},
    creditLimit: customerProfile?.grossCreditLimit || '',
    customerName: resolvedCustomerName,
    lastCheckin: customerProfile?.latestCheckinDate || '',

    hasLatestInvoice: !isEmpty(customerProfile?.latestInvoiceId),
    latestInvoice: {
      date: customerProfile?.latestInvoiceDate || '',
      invoiceName: customerProfile?.latestInvoiceNumber || '',
      invoiceValue: Number(customerProfile?.latestInvoiceAmount) || 0,
      invoiceId: customerProfile?.latestInvoiceId ?? '',
    },
    hasLatestPayment: !isEmpty(customerProfile?.latestPaymentId),
    latestPayment: {
      date: '', //customerProfile?.latestPaymentDate ||
      paymentName: customerProfile?.latestPaymentNumber || '',
      paymentValue: Number(customerProfile?.latestPaymentAmount) || 0,
      paymentId: customerProfile?.latestPaymentId ?? '',
      createdBy: '',
    },
    hasLatestOrder: !isEmpty(customerProfile?.latestOrderId),
    latestOrder: {
      date: customerProfile?.latestOrderDate ?? '',
      refOrderNumber: customerProfile?.latestOrderNumber ?? '',
      totalValue: Number(customerProfile?.latestOrderAmount) ?? 0,
      orderId: customerProfile?.latestOrderId ?? '',
      createdBy: customerProfile?.latestOrderCreatedBy ?? '',
    },
    activeConversations,
    mobileNumber: resolvedMobileNumber,
    modules,
    outStandingAmount: Number(customerProfile?.outstandingAmount) || 0,
    onBack,
    lastUpdateAt: customerProfileData?.latestLedgerUpdatedAt,
    salesRepName: '', //customerProfile?.salesTeamMemberName ||
    isClockedIn: clockInInfo?.tripId,
    isOnline,
    isCustomerCheckedIn: checkInInfo?.customerId === customerId,
    alreadyCheckedInCustomerExist:
      checkInInfo?.customerId && checkInInfo?.customerId !== customerId,
    phone: customerDetails?.mobile ?? '',
    loading,
    refetchProfile: fetchCustomerProfile,
  };
};
